import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "../../button/button";
import Select from "../../select/select";
import TableCstmPs from "../../TableCstmPs/TableCstmPs";
import Tooltip from "../../Tooltip/Tooltip";
import Input from "../../../DesignSystem/Atoms/Input/Input";
import Autocomplete from "../../../DesignSystem/Molecules/Autocomplete/Autocomplete";
import AddSVG from "../../icons/add";
import CopySVG from "../../icons/copy";
import EditSVG from "../../icons/edit";
import TrashSVG from "../../icons/trash";
import UpdateSVG from "../../icons/update";
import FowardArrowSVG from "../../icons/fowardArrow";
import LoadingComponent from "../../loading/LoadingComponent/loadingComponent";
import ConfirmModal from "../../ConfirmModalV3/confirmModal";
import MaterialCorrectionTrackingLinkModal from "./materialCorrectionTrackingLinkModal/MaterialCorrectionTrackingLinkModal";
import {
    GetMaterialCorrectionTrackingLinks,
    PatchMaterialCorrectionTrackingLink,
    PatchMaterialCorrectionTrackingLinkStatus,
    PostMaterialCorrectionTrackingLink,
} from "../../../utils/Apis/principalApis";
import "./MaterialCorrectionTrackingLinks.scss";

const STATUS_OPTIONS = [
    { label: "Activos", value: "active" },
    { label: "Archivados", value: "archived" },
    { label: "Todos", value: "all" },
];

const formatDate = (dateValue) => {
    if (!dateValue) return "Sin fecha";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return dateValue;

    return new Intl.DateTimeFormat("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "America/Tijuana",
    }).format(date);
};

const MaterialCorrectionTrackingLinks = ({ cacheTrackingLinks, showAlert }) => {
    const [trackingLinks, setTrackingLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchValue, setSearchValue] = useState("");
    const [employeeSelected, setEmployeeSelected] = useState(null);
    const [statusFilter, setStatusFilter] = useState("active");
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("add");
    const [trackingLinkSelected, setTrackingLinkSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [changingStatus, setChangingStatus] = useState(false);
    const savingRef = useRef(false);
    const changingStatusRef = useRef(false);
    const showAlertRef = useRef(showAlert);

    useEffect(() => {
        showAlertRef.current = showAlert;
    }, [showAlert]);

    const fetchTrackingLinks = useCallback(async (forceUpdate = false) => {
        const cacheKey = "all";

        setLoading(true);

        try {
            if (forceUpdate) {
                delete cacheTrackingLinks.current[cacheKey];
            }

            if (!forceUpdate && cacheTrackingLinks.current[cacheKey]) {
                setTrackingLinks(cacheTrackingLinks.current[cacheKey]);
                return;
            }

            const response = await GetMaterialCorrectionTrackingLinks();

            if (response?.auth === false) {
                window.location.reload();
                return;
            }

            if (response?.status !== 200) {
                showAlertRef.current(
                    "danger",
                    response?.message || "No fue posible obtener los enlaces de seguimiento de corrección de material"
                );
                return;
            }

            const links = Array.isArray(response.result) ? response.result : [];

            cacheTrackingLinks.current[cacheKey] = links;
            setTrackingLinks(links);
        } catch (error) {
            console.error("Error obteniendo enlaces de seguimiento de corrección de material:", error);
            showAlertRef.current("danger", "No fue posible obtener los enlaces de seguimiento de corrección de material");
        } finally {
            setLoading(false);
        }
    }, [cacheTrackingLinks]);

    useEffect(() => {
        fetchTrackingLinks();
    }, [fetchTrackingLinks]);

    const handleOpenAddModal = () => {
        setModalMode("add");
        setTrackingLinkSelected(null);
        setModalOpen(true);
    };

    const handleOpenEditModal = (trackingLink) => {
        setModalMode("edit");
        setTrackingLinkSelected(trackingLink);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        if (savingRef.current) return;

        setModalOpen(false);
        setTrackingLinkSelected(null);
    };

    const handleSaveTrackingLink = async ({ folio, tracking_url }) => {
        if (savingRef.current) return;

        const isEditMode = modalMode === "edit";

        if (isEditMode && !trackingLinkSelected?.id) {
            showAlertRef.current("danger", "No fue posible identificar el enlace a editar");
            return;
        }

        savingRef.current = true;
        setSaving(true);

        try {
            const response = isEditMode
                ? await PatchMaterialCorrectionTrackingLink(
                    trackingLinkSelected.id,
                    folio,
                    tracking_url
                )
                : await PostMaterialCorrectionTrackingLink(folio, tracking_url);

            if (response?.auth === false) {
                window.location.reload();
                return;
            }

            const expectedStatus = isEditMode ? 200 : 201;

            if (response?.status !== expectedStatus) {
                if (isEditMode && response?.status === 404) {
                    setModalOpen(false);
                    setTrackingLinkSelected(null);
                    delete cacheTrackingLinks.current.all;
                    await fetchTrackingLinks(true);
                }

                showAlertRef.current(
                    "danger",
                    response?.message ||
                    `No fue posible ${isEditMode ? "actualizar" : "registrar"} el enlace de seguimiento de corrección de material`
                );
                return;
            }

            setModalOpen(false);
            setTrackingLinkSelected(null);
            delete cacheTrackingLinks.current.all;
            await fetchTrackingLinks(true);
            showAlertRef.current(
                "success",
                response.message ||
                `Enlace ${isEditMode ? "actualizado" : "agregado"} correctamente`
            );
        } catch (error) {
            console.error(
                `Error ${isEditMode ? "actualizando" : "registrando"} enlace de seguimiento de corrección de material:`,
                error
            );
            showAlertRef.current(
                "danger",
                `No fue posible ${isEditMode ? "actualizar" : "registrar"} el enlace de seguimiento de corrección de material`
            );
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const handleOpenStatusConfirm = (trackingLink) => {
        setTrackingLinkSelected(trackingLink);
        setPendingStatus(trackingLink.st === 1 ? 0 : 1);
        setConfirmStatusOpen(true);
    };

    const handleCloseStatusConfirm = () => {
        if (changingStatusRef.current) return;

        setConfirmStatusOpen(false);
        setTrackingLinkSelected(null);
        setPendingStatus(null);
    };

    const handleConfirmStatusChange = async () => {
        if (changingStatusRef.current) return;

        if (!trackingLinkSelected?.id || (pendingStatus !== 0 && pendingStatus !== 1)) {
            showAlertRef.current(
                "danger",
                "No fue posible identificar el enlace o el estado solicitado"
            );
            return;
        }

        changingStatusRef.current = true;
        setChangingStatus(true);

        try {
            const response = await PatchMaterialCorrectionTrackingLinkStatus(
                trackingLinkSelected.id,
                pendingStatus
            );

            if (response?.auth === false) {
                window.location.reload();
                return;
            }

            if (response?.status !== 200) {
                if (response?.status === 404) {
                    setConfirmStatusOpen(false);
                    setTrackingLinkSelected(null);
                    setPendingStatus(null);
                    delete cacheTrackingLinks.current.all;
                    await fetchTrackingLinks(true);
                }

                showAlertRef.current(
                    "danger",
                    response?.message ||
                    "No fue posible cambiar el estado del enlace de seguimiento de corrección de material"
                );
                return;
            }

            setConfirmStatusOpen(false);
            setTrackingLinkSelected(null);
            setPendingStatus(null);
            delete cacheTrackingLinks.current.all;
            await fetchTrackingLinks(true);
            showAlertRef.current(
                "success",
                response.message || "Estado del enlace actualizado correctamente"
            );
        } catch (error) {
            console.error(
                "Error cambiando estado de enlace de seguimiento de corrección de material:",
                error
            );
            showAlertRef.current(
                "danger",
                "No fue posible cambiar el estado del enlace de seguimiento de corrección de material"
            );
        } finally {
            changingStatusRef.current = false;
            setChangingStatus(false);
        }
    };

    const employeeOptions = useMemo(() => {
        const employees = new Map();

        trackingLinks.forEach((trackingLink) => {
            const employee = trackingLink.created_by;

            if (employee?.employee_id && !employees.has(employee.employee_id)) {
                employees.set(employee.employee_id, employee);
            }
        });

        return Array.from(employees.values()).sort((firstEmployee, secondEmployee) =>
            firstEmployee.name.localeCompare(secondEmployee.name, "es")
        );
    }, [trackingLinks]);

    const filteredTrackingLinks = useMemo(() => {
        const normalizedSearch = searchValue.trim().toLocaleLowerCase("es");

        return trackingLinks.filter((trackingLink) => {
            const matchesFolio = trackingLink.folio
                .toLocaleLowerCase("es")
                .includes(normalizedSearch);
            const matchesEmployee = !employeeSelected ||
                trackingLink.created_by.employee_id === employeeSelected.employee_id;
            const matchesStatus =
                statusFilter === "all" ||
                (statusFilter === "active" && trackingLink.st === 1) ||
                (statusFilter === "archived" && trackingLink.st === 0);

            return matchesFolio && matchesEmployee && matchesStatus;
        });
    }, [trackingLinks, searchValue, employeeSelected, statusFilter]);

    const handleOpenExternalLink = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleCopyLink = async (url) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = url;
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }

            showAlert("success", "Enlace copiado al portapapeles");
        } catch (error) {
            showAlert("danger", "No fue posible copiar el enlace");
        }
    };

    const columns = [
        {
            header: "Folio",
            accessor: "folio",
            className: "material-correction-tracking-links__folio-column",
            render: (folio) => <strong>{folio}</strong>,
        },
        {
            header: "Link de seguimiento",
            accessor: "tracking_url",
            className: "material-correction-tracking-links__url-column",
            render: (url) => (
                <Tooltip text={url} position="top">
                    <button
                        type="button"
                        className="material-correction-tracking-links__url"
                        onClick={() => handleOpenExternalLink(url)}
                    >
                        {url}
                    </button>
                </Tooltip>
            ),
        },
        {
            header: "Registrado por",
            accessor: "created_by.name",
            className: "tableCtype4",
        },
        {
            header: "Fecha de registro",
            accessor: "created_at",
            className: "tableCtype3",
            render: (createdAt) => formatDate(createdAt),
        },
        {
            header: "Estado",
            accessor: "st",
            className: "tableCtype2 align-center",
            render: (status) => (
                <span className={`material-correction-tracking-links__status ${status === 1 ? "is-active" : "is-archived"}`}>
                    {status === 1 ? "Activo" : "Archivado"}
                </span>
            ),
        },
        {
            header: "Acciones",
            id: "actions",
            className: "material-correction-tracking-links__actions-column",
            render: (_, trackingLink) => (
                <div className="material-correction-tracking-links__actions">
                    <Tooltip text="Abrir enlace" position="top">
                        <Button
                            text=""
                            Icon={FowardArrowSVG}
                            backgroundColor="transparent"
                            className="material-correction-tracking-links__action-button open"
                            onClick={() => handleOpenExternalLink(trackingLink.tracking_url)}
                        />
                    </Tooltip>
                    <Tooltip text="Copiar enlace" position="top">
                        <Button
                            text=""
                            Icon={CopySVG}
                            backgroundColor="transparent"
                            className="material-correction-tracking-links__action-button copy"
                            onClick={() => handleCopyLink(trackingLink.tracking_url)}
                        />
                    </Tooltip>
                    <Tooltip text="Editar enlace" position="top">
                        <Button
                            text=""
                            Icon={EditSVG}
                            backgroundColor="transparent"
                            className="material-correction-tracking-links__action-button edit"
                            onClick={() => handleOpenEditModal(trackingLink)}
                        />
                    </Tooltip>
                    <Tooltip
                        text={`${trackingLink.st === 1 ? "Archivar" : "Reactivar"} enlace`}
                        position="left"
                    >
                        <Button
                            text=""
                            Icon={trackingLink.st === 1 ? TrashSVG : UpdateSVG}
                            backgroundColor="transparent"
                            className={`material-correction-tracking-links__action-button ${trackingLink.st === 1 ? "archive" : "reactivate"}`}
                            onClick={() => handleOpenStatusConfirm(trackingLink)}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="material-correction-tracking-links main-color">
                <header className="material-correction-tracking-links__header">
                    <div>
                        <h1 className="title-1">Seguimiento de corrección de material</h1>
                        <p>Administra los enlaces externos para consultar el seguimiento de cada corrección de material.</p>
                    </div>
                    <Button
                        text="Agregar enlace de seguimiento"
                        Icon={AddSVG}
                        onClick={handleOpenAddModal}
                    />
                </header>

                <section className="material-correction-tracking-links__filters">
                    <Input
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        placeholder="Buscar por folio"
                    />
                    <Autocomplete
                        options={employeeOptions}
                        value={employeeSelected}
                        onChange={setEmployeeSelected}
                        placeholder="Registrado por"
                        getOptionLabel={(employee) => employee.name}
                        getSearchText={(employee) => employee.name}
                        isOptionEqualToValue={(option, value) =>
                            option.employee_id === value.employee_id
                        }
                        noOptionsText="Sin empleados registrados"
                    />
                    <div className="material-correction-tracking-links__status-filter">
                        <label htmlFor="material-correction-tracking-status">Estado</label>
                        <Select
                            value={statusFilter}
                            options={STATUS_OPTIONS}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            placeholder="Selecciona un estado"
                        />
                    </div>
                </section>

                <section className="material-correction-tracking-links__table">
                    {loading ? (
                        <LoadingComponent text="Cargando enlaces de seguimiento..." />
                    ) : (
                        <>
                            <div className="material-correction-tracking-links__results">
                                {filteredTrackingLinks.length} resultado{filteredTrackingLinks.length === 1 ? "" : "s"}
                            </div>
                            <TableCstmPs
                                columns={columns}
                                data={filteredTrackingLinks}
                                className="material-correction-tracking-links__data-table"
                                emptyMessage="No se encontraron enlaces con los filtros seleccionados"
                                variant="modern"
                                rowClassName={(trackingLink) => trackingLink.st === 0 ? "is-archived-row" : ""}
                                styleOptions={{
                                    hoverable: true,
                                    rounded: true,
                                    shadow: true,
                                }}
                                layout={{
                                    stickyHeader: true,
                                    borderMode: "separate",
                                }}
                            />
                        </>
                    )}
                </section>
            </div>

            <MaterialCorrectionTrackingLinkModal
                isOpen={modalOpen}
                mode={modalMode}
                trackingLink={trackingLinkSelected}
                trackingLinks={trackingLinks}
                currentEmployee={{ name: "Se asignará al guardar" }}
                onClose={handleCloseModal}
                onSave={handleSaveTrackingLink}
                showAlert={showAlert}
                saving={saving}
            />
            <ConfirmModal
                isOpen={confirmStatusOpen}
                handleCancel={handleCloseStatusConfirm}
                handleAccept={handleConfirmStatusChange}
                title={pendingStatus === 0 ? "Archivar enlace" : "Reactivar enlace"}
                text={
                    pendingStatus === 0
                        ? "¿Deseas archivar este enlace de seguimiento de corrección de material?"
                        : "¿Deseas reactivar este enlace de seguimiento de corrección de material?"
                }
                disabled={changingStatus}
                continueButtonText={
                    changingStatus
                        ? "Procesando..."
                        : pendingStatus === 0
                            ? "Archivar"
                            : "Reactivar"
                }
            />
        </>
    );
};

export default MaterialCorrectionTrackingLinks;
